import React, { useEffect, useState } from "react";
import { api, formatDateTime, formatErr, clearToken } from "../../lib/api";
import { toast } from "sonner";
import { Brain, CreditCard, CheckCircle, XCircle, Warning, Circle, ToggleLeft, ToggleRight, Plugs, ShieldCheck, WarningOctagon, WhatsappLogo, QrCode, ArrowsClockwise, Eye, EyeSlash } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    active: { label: "Ativa", cls: "green", Icon: CheckCircle },
    invalid: { label: "Invalida", cls: "red", Icon: XCircle },
    untested: { label: "Nao testada", cls: "medio", Icon: Warning },
    unconfigured: { label: "Nao configurada", cls: "neutral", Icon: Circle },
  };
  const s = map[status] || map.unconfigured;
  const Icon = s.Icon;
  return (
    <span className={`badge ${s.cls} inline-flex items-center gap-1`} data-testid="integration-status">
      <Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [openaiKey, setOpenaiKey] = useState("");
  const [model, setModel] = useState("gpt-4o-mini");
  const [mpToken, setMpToken] = useState("");
  const [zapiBaseUrl, setZapiBaseUrl] = useState("https://api.z-api.io");
  const [zapiInstanceId, setZapiInstanceId] = useState("");
  const [zapiToken, setZapiToken] = useState("");
  const [zapiClientToken, setZapiClientToken] = useState("");
  const [zapiWebhook, setZapiWebhook] = useState("");
  const [zapiQr, setZapiQr] = useState("");
  const [zapiStatusInfo, setZapiStatusInfo] = useState(null);
  const [zapiQrPolling, setZapiQrPolling] = useState(false);
  const [zapiDiag, setZapiDiag] = useState(null);
  const [zapiTestPhone, setZapiTestPhone] = useState("");
  const [zapiTestResult, setZapiTestResult] = useState(null);
  const [showZapiSecrets, setShowZapiSecrets] = useState(false);
  const [mode, setMode] = useState("platform");
  const [busy, setBusy] = useState("");

  // Security / admin credentials
  const [minPw, setMinPw] = useState(8);
  const [curPwE, setCurPwE] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [curPwP, setCurPwP] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/integrations/settings");
      setCfg(data);
      setModel(data.openai.model || "gpt-4o-mini");
      setMode(data.pix_mode || "platform");
      setZapiBaseUrl(data.zapi?.base_url || "https://api.z-api.io");
      setZapiInstanceId(data.zapi?.instance_id || "");
      setZapiWebhook(data.zapi?.webhook_url || "");
    } catch (e) {
      setCfg(null);
      const status = e.response?.status;
      setErr(
        status === 404
          ? "Endpoint de configuracoes nao encontrado. O backend pode estar desatualizado nesta instalacao - rode o deploy/atualize a VPS."
          : (formatErr(e.response?.data?.detail) || "Nao foi possivel carregar as configuracoes. Verifique sua conexao e tente novamente.")
      );
      return;
    }
    // Nao bloqueia a tela se as configuracoes de seguranca falharem
    try { setMinPw((await api.get("/auth/security-settings")).data.min_password_length); } catch {}
  };
  useEffect(() => { load(); }, []);

  // Enquanto o QR do numero oficial estiver na tela, verifica a conexao a cada 5s.
  useEffect(() => {
    if (!zapiQrPolling) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/integrations/zapi/status");
        setZapiStatusInfo(data);
        if (data.connected) { setZapiQr(""); setZapiQrPolling(false); clearInterval(t); toast.success("Numero oficial conectado!"); load(); }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zapiQrPolling]);

  const forceRelogin = (msg) => {
    toast.success(msg);
    clearToken();
    setTimeout(() => window.location.assign("/login"), 1200);
  };

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(""); }
  };

  const saveOpenAI = () => run("oa-save", async () => {
    await api.put("/integrations/openai", { api_key: openaiKey || undefined, model });
    setOpenaiKey("");
  }, "Configuracao da IA salva");

  const testOpenAI = () => run("oa-test", async () => {
    const { data } = await api.post("/integrations/openai/test", { token: openaiKey || undefined });
    data.valid ? toast.success(data.message) : toast.error(data.message);
  });

  const toggleOpenAI = () => run("oa-toggle", async () => {
    await api.put("/integrations/openai", { enabled: !cfg.openai.enabled });
  });

  const saveMP = () => run("mp-save", async () => {
    await api.put("/integrations/mercadopago", { access_token: mpToken || undefined });
    setMpToken("");
  }, "Token do Mercado Pago salvo");

  const testMP = () => run("mp-test", async () => {
    const { data } = await api.post("/integrations/mercadopago/test", { token: mpToken || undefined });
    data.valid ? toast.success(data.message) : toast.error(data.message);
  });

  const toggleMP = () => run("mp-toggle", async () => {
    await api.put("/integrations/mercadopago", { enabled: !cfg.mercadopago.enabled });
  });

  const saveZapi = () => run("zapi-save", async () => {
    await api.put("/integrations/zapi", {
      base_url: zapiBaseUrl || undefined,
      instance_id: zapiInstanceId || undefined,
      instance_token: zapiToken || undefined,
      client_token: zapiClientToken || undefined,
      webhook_url: zapiWebhook || undefined,
    });
    setZapiToken(""); setZapiClientToken("");
  }, "Configuracao do WhatsApp oficial (Z-API) salva");

  const testZapi = () => run("zapi-test", async () => {
    const { data } = await api.post("/integrations/zapi/test");
    data.valid ? toast.success(data.message) : toast.error(data.message);
  });

  const toggleZapi = () => run("zapi-toggle", async () => {
    await api.put("/integrations/zapi", { enabled: !cfg.zapi.enabled });
  });

  const loadZapiStatus = async () => {
    try {
      const { data } = await api.get("/integrations/zapi/status");
      setZapiStatusInfo(data);
      if (data.connected) { setZapiQr(""); setZapiQrPolling(false); toast.success("Numero oficial conectado!"); await load(); }
      return data;
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Erro ao consultar status"); return null; }
  };

  const showZapiQr = async () => {
    setBusy("zapi-qr"); setZapiQr("");
    try {
      const { data } = await api.get("/integrations/zapi/qr");
      if (data.connected) { toast.success("Numero ja conectado!"); await load(); }
      else if (data.qr_base64) { setZapiQr(data.qr_base64); setZapiQrPolling(true); }
      else { toast.message(data.detail || "QR Code indisponivel. Tente novamente."); }
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Erro ao obter o QR Code"); }
    finally { setBusy(""); }
  };

  const configureWebhook = () => run("zapi-webhook", async () => {
    const { data } = await api.post("/integrations/zapi/configure-webhook", { webhook_url: zapiWebhook || undefined });
    toast.success(`Webhook configurado: ${data.webhook_url}`);
  });

  const loadDiag = async () => {
    try {
      const { data } = await api.get("/integrations/zapi/diagnostics");
      setZapiDiag(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Erro ao carregar diagnostico"); }
  };

  const sendTest = () => run("zapi-sendtest", async () => {
    setZapiTestResult(null);
    if (!zapiTestPhone.trim()) { toast.error("Informe um numero para o teste"); return; }
    const { data } = await api.post("/integrations/zapi/send-test", { phone: zapiTestPhone, message: "Teste de conexao Z-API - Cris Lingerie" });
    setZapiTestResult(data);
    await loadDiag();
    if (data.ok) toast.success(`Enviado! messageId: ${data.message_id || "-"}`);
    else toast.error(formatErr(data.detail) || "Falha no envio");
  });

  const saveMode = (newMode) => run("mode", async () => {
    await api.put("/integrations/pix-mode", { mode: newMode });
    setMode(newMode);
  }, "Modo de operacao do PIX atualizado");

  const saveMinPw = () => run("minpw", async () => {
    const { data } = await api.put("/auth/security-settings", { min_password_length: Number(minPw) });
    setMinPw(data.min_password_length);
  }, "Senha minima atualizada");

  const changeEmail = async () => {
    if (!curPwE || !newEmail) return toast.error("Preencha a senha atual e o novo e-mail");
    setBusy("email");
    try {
      const { data } = await api.post("/auth/change-email", { current_password: curPwE, new_email: newEmail });
      forceRelogin(data.message || "E-mail atualizado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); setBusy(""); }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) return toast.error("A confirmacao nao confere");
    setBusy("pw");
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: curPwP, new_password: newPw, confirm_password: confirmPw,
      });
      forceRelogin(data.message || "Senha atualizada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); setBusy(""); }
  };

  if (!cfg) {
    if (err) {
      return (
        <div data-testid="admin-settings-error" className="max-w-xl">
          <div className="topbar">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Sistema</div>
              <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Configuracoes</h1>
            </div>
          </div>
          <div className="border border-[#DDA7A5] bg-[#F4E6E5] p-6 flex items-start gap-3">
            <WarningOctagon size={26} weight="duotone" className="shrink-0 text-[#7A2D2B]" />
            <div>
              <div className="font-display text-lg font-bold text-[#7A2D2B]">Erro ao carregar configuracoes</div>
              <div className="text-sm text-[#7A2D2B] mt-1">{err}</div>
              <button onClick={load} className="brand mt-4" data-testid="settings-retry-btn">Tentar novamente</button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="settings-loading">Carregando...</div>;
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Sistema</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Configuracoes</h1>
        </div>
        <Plugs size={42} weight="duotone" />
      </div>

      {/* IA / OpenAI */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="openai-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Brain size={26} weight="duotone" />
            <div>
              <h2 className="section-title">IA / OpenAI</h2>
              <div className="text-xs text-[#5C5A57] mt-1">Mensagens do CRM e analise de clientes. Fallback por regras quando desativada.</div>
            </div>
          </div>
          <StatusBadge status={cfg.openai.status} />
        </div>

        {cfg.openai.configured && (
          <div className="text-xs text-[#5C5A57] mb-3 tabular">
            Chave salva: <strong>{cfg.openai.masked}</strong>
            {cfg.openai.last_tested_at && <> · testada em {formatDateTime(cfg.openai.last_tested_at)}</>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">
              {cfg.openai.configured ? "Substituir chave (OPENAI_API_KEY)" : "Cadastrar chave (OPENAI_API_KEY)"}
            </label>
            <input type="password" autoComplete="off" className="input-underline" placeholder="sk-..."
              value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} data-testid="openai-key-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Modelo</label>
            <select className="input-underline" value={model} onChange={(e) => setModel(e.target.value)} data-testid="openai-model-select">
              <option value="gpt-4o-mini">gpt-4o-mini</option>
              <option value="gpt-4o">gpt-4o</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={saveOpenAI} disabled={busy === "oa-save"} className="brand" data-testid="openai-save-btn">{busy === "oa-save" ? "Salvando..." : "Salvar"}</button>
          <button onClick={testOpenAI} disabled={busy === "oa-test"} className="brand" style={{ background: "#5C5A57" }} data-testid="openai-test-btn">{busy === "oa-test" ? "Testando..." : "Testar conexao"}</button>
          <button onClick={toggleOpenAI} disabled={busy === "oa-toggle" || !cfg.openai.configured} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium hover:underline disabled:opacity-40" data-testid="openai-toggle-btn">
            {cfg.openai.enabled ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
            {cfg.openai.enabled ? "Ativada" : "Desativada"}
          </button>
        </div>
      </section>

      {/* Pagamentos / Mercado Pago */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="mercadopago-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <CreditCard size={26} weight="duotone" />
            <div>
              <h2 className="section-title">Pagamentos / Mercado Pago</h2>
              <div className="text-xs text-[#5C5A57] mt-1">Access Token da plataforma para cobrancas PIX.</div>
            </div>
          </div>
          <StatusBadge status={cfg.mercadopago.status} />
        </div>

        {cfg.mercadopago.configured && (
          <div className="text-xs text-[#5C5A57] mb-3 tabular">
            Token salvo: <strong>{cfg.mercadopago.masked}</strong>
            {cfg.mercadopago.last_tested_at && <> · testado em {formatDateTime(cfg.mercadopago.last_tested_at)}</>}
          </div>
        )}

        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">
          {cfg.mercadopago.configured ? "Substituir Access Token" : "Cadastrar Access Token"}
        </label>
        <input type="password" autoComplete="off" className="input-underline" placeholder="APP_USR-... ou TEST-..."
          value={mpToken} onChange={(e) => setMpToken(e.target.value)} data-testid="mp-token-input" />

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={saveMP} disabled={busy === "mp-save"} className="brand" data-testid="mp-save-btn">{busy === "mp-save" ? "Salvando..." : "Salvar"}</button>
          <button onClick={testMP} disabled={busy === "mp-test"} className="brand" style={{ background: "#5C5A57" }} data-testid="mp-test-btn">{busy === "mp-test" ? "Testando..." : "Testar conexao"}</button>
          <button onClick={toggleMP} disabled={busy === "mp-toggle" || !cfg.mercadopago.configured} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium hover:underline disabled:opacity-40" data-testid="mp-toggle-btn">
            {cfg.mercadopago.enabled ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
            {cfg.mercadopago.enabled ? "Ativado" : "Desativado"}
          </button>
        </div>
      </section>

      {/* WhatsApp Oficial / Z-API (numero unico centralizado) */}
      <section className="border border-[#3F6B45] bg-white p-6 mb-6" data-testid="zapi-section">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <WhatsappLogo size={26} weight="duotone" color="#25D366" />
            <div>
              <h2 className="section-title">WhatsApp Oficial (Z-API)</h2>
              <div className="text-xs text-[#5C5A57] mt-1">Um unico numero oficial da empresa para todas as cobrancas, lembretes, confirmacoes e atendimentos. A revendedora responsavel por cada cliente e identificada automaticamente pelo cadastro.</div>
            </div>
          </div>
          <StatusBadge status={cfg.zapi?.status} />
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`badge ${cfg.zapi?.connected ? "green" : "neutral"} inline-flex items-center gap-1`} data-testid="zapi-connection-badge">
            <Circle size={10} weight="fill" /> {cfg.zapi?.connected ? `Conectado${cfg.zapi?.connected_phone ? " · " + cfg.zapi.connected_phone : ""}` : "Numero nao conectado"}
          </span>
          <button onClick={loadZapiStatus} className="text-[10px] tracking-widest uppercase font-medium hover:underline inline-flex items-center gap-1" data-testid="zapi-refresh-status-btn">
            <ArrowsClockwise size={13} /> Atualizar status
          </button>
        </div>

        {cfg.zapi?.configured && (
          <div className="text-xs text-[#5C5A57] mb-3 tabular">
            Instance Token: <strong>{cfg.zapi.masked}</strong> · Client-Token: <strong>{cfg.zapi.masked_client || "-"}</strong>
            {cfg.zapi.last_tested_at && <> · testado em {formatDateTime(cfg.zapi.last_tested_at)}</>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Base URL</label>
            <input type="text" autoComplete="off" className="input-underline" placeholder="https://api.z-api.io"
              value={zapiBaseUrl} onChange={(e) => setZapiBaseUrl(e.target.value)} data-testid="zapi-baseurl-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Instance ID</label>
            <input type="text" autoComplete="off" className="input-underline" placeholder="ID da instancia Z-API"
              value={zapiInstanceId} onChange={(e) => setZapiInstanceId(e.target.value)} data-testid="zapi-instanceid-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57]">
                {cfg.zapi?.configured ? "Substituir Instance Token" : "Instance Token"}
              </label>
              <button type="button" onClick={() => setShowZapiSecrets((s) => !s)} className="text-[10px] tracking-widest uppercase text-[#5C5A57] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="zapi-toggle-secrets">
                {showZapiSecrets ? <><EyeSlash size={12} /> Ocultar</> : <><Eye size={12} /> Mostrar</>}
              </button>
            </div>
            <input type={showZapiSecrets ? "text" : "password"} autoComplete="new-password" name="zapi_instance_token_nofill"
              data-lpignore="true" data-1p-ignore="true" data-form-type="other" className="input-underline" placeholder="token da instancia"
              value={zapiToken} onChange={(e) => setZapiToken(e.target.value.trim())} data-testid="zapi-token-input" />
            {zapiToken && <div className="text-[10px] text-[#5C5A57] mt-1" data-testid="zapi-token-count">{zapiToken.length} caracteres digitados</div>}
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">
              {cfg.zapi?.configured ? "Substituir Client-Token" : "Client-Token (seguranca da conta)"}
            </label>
            <input type={showZapiSecrets ? "text" : "password"} autoComplete="new-password" name="zapi_client_token_nofill"
              data-lpignore="true" data-1p-ignore="true" data-form-type="other" className="input-underline" placeholder="account security token"
              value={zapiClientToken} onChange={(e) => setZapiClientToken(e.target.value.trim())} data-testid="zapi-clienttoken-input" />
            {zapiClientToken && <div className="text-[10px] text-[#3F6B45] mt-1" data-testid="zapi-clienttoken-count">{zapiClientToken.length} caracteres digitados (sera salvo ao clicar em Salvar)</div>}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">URL de Webhook (recebimento)</label>
          <input type="text" autoComplete="off" className="input-underline" placeholder="https://crislingeries.com.br/api/whatsapp/webhook"
            value={zapiWebhook} onChange={(e) => setZapiWebhook(e.target.value)} data-testid="zapi-webhook-input" />
          <div className="text-[11px] text-[#5C5A57] mt-1">Endereco que a Z-API chamara ao receber mensagens, comprovantes e status. Salve e clique em Configurar webhook para registrar automaticamente na Z-API.</div>
        </div>

        <div className="flex flex-wrap gap-3 mt-5">
          <button onClick={saveZapi} disabled={busy === "zapi-save"} className="brand" data-testid="zapi-save-btn">{busy === "zapi-save" ? "Salvando..." : "Salvar"}</button>
          <button onClick={testZapi} disabled={busy === "zapi-test"} className="brand" style={{ background: "#5C5A57" }} data-testid="zapi-test-btn">{busy === "zapi-test" ? "Testando..." : "Testar conexao"}</button>
          <button onClick={showZapiQr} disabled={busy === "zapi-qr" || !cfg.zapi?.configured} className="brand disabled:opacity-40 inline-flex items-center gap-2" style={{ background: "#3F6B45" }} data-testid="zapi-qr-btn"><QrCode size={16} />{busy === "zapi-qr" ? "Gerando QR..." : "Conectar (QR Code)"}</button>
          <button onClick={configureWebhook} disabled={busy === "zapi-webhook" || !cfg.zapi?.configured} className="brand disabled:opacity-40" style={{ background: "#5C5A57" }} data-testid="zapi-webhook-btn">{busy === "zapi-webhook" ? "Configurando..." : "Configurar webhook"}</button>
          <button onClick={toggleZapi} disabled={busy === "zapi-toggle" || !cfg.zapi?.configured} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium hover:underline disabled:opacity-40" data-testid="zapi-toggle-btn">
            {cfg.zapi?.enabled ? <ToggleRight size={22} weight="fill" /> : <ToggleLeft size={22} />}
            {cfg.zapi?.enabled ? "Ativado" : "Desativado"}
          </button>
        </div>

        {/* Bloco de diagnostico seguro + Enviar teste */}
        <div className="mt-5 border border-[#EBE5DF] bg-[#FAF7F3] p-4" data-testid="zapi-diagnostics-block">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] tracking-widest uppercase text-[#5C5A57] font-medium">Diagnostico (seguro)</span>
            <button onClick={loadDiag} className="text-[10px] tracking-widest uppercase font-medium hover:underline inline-flex items-center gap-1" data-testid="zapi-diag-refresh-btn"><ArrowsClockwise size={12} /> Carregar</button>
          </div>
          {zapiDiag ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs tabular" data-testid="zapi-diag-data">
              <div className="flex justify-between"><span className="text-[#5C5A57]">Base URL</span><strong>{zapiDiag.base_url}</strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Instance ID</span><strong>{zapiDiag.instance_id_masked || "-"} <span className="text-[#5C5A57]">({zapiDiag.instance_id_len})</span></strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Instance Token</span><strong>{zapiDiag.instance_token_masked_atual || "-"} <span className="text-[#5C5A57]">({zapiDiag.instance_token_len})</span></strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Client-Token</span><strong className={zapiDiag.client_token_configured ? "text-[#3F6B45]" : "text-[#B4564F]"}>{zapiDiag.client_token_configured ? `sim (${zapiDiag.client_token_len})` : "nao"}</strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Instance Token decifrado</span><strong className={zapiDiag.instance_token_decrypt_ok ? "text-[#3F6B45]" : "text-[#B4564F]"}>{zapiDiag.instance_token_decrypt_ok ? "ok" : "FALHOU"}</strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Status do Client-Token</span><strong className={zapiDiag.client_token_status === "ok" ? "text-[#3F6B45]" : "text-[#B4564F]"}>{zapiDiag.client_token_status}</strong></div>
              {(zapiDiag.instance_token_has_spaces || zapiDiag.instance_id_has_spaces) && <div className="md:col-span-2 text-[#B4564F]">Atencao: ha espacos no Instance ID/Token salvo.</div>}
              <div className="md:col-span-2 flex justify-between border-t border-[#EBE5DF] pt-1 mt-1"><span className="text-[#5C5A57]">Endpoint final</span><strong className="text-right break-all">{zapiDiag.endpoint_masked}</strong></div>
              <div className="flex justify-between"><span className="text-[#5C5A57]">Ultimo HTTP</span><strong>{zapiDiag.last_http_status ?? "-"}</strong></div>
              <div className="md:col-span-2 flex justify-between"><span className="text-[#5C5A57]">Ultimo body Z-API</span><strong className="text-right break-all">{zapiDiag.last_body || "-"}</strong></div>
            </div>
          ) : <div className="text-xs text-[#5C5A57]">Clique em Carregar para ver o que esta salvo no banco (mascarado).</div>}

          <div className="mt-4 pt-3 border-t border-[#EBE5DF]">
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Enviar teste (mesma funcao do CRM)</label>
            <div className="flex flex-wrap gap-2 items-center">
              <input type="text" autoComplete="off" className="input-underline flex-1 min-w-[200px]" placeholder="5511969011024 ou (11) 96901-1024"
                value={zapiTestPhone} onChange={(e) => setZapiTestPhone(e.target.value)} data-testid="zapi-sendtest-input" />
              <button onClick={sendTest} disabled={busy === "zapi-sendtest"} className="brand" style={{ background: "#3F6B45" }} data-testid="zapi-sendtest-btn">{busy === "zapi-sendtest" ? "Enviando..." : "Enviar teste"}</button>
            </div>
            {zapiTestResult && (
              <div className={`mt-2 text-xs tabular ${zapiTestResult.ok ? "text-[#3F6B45]" : "text-[#B4564F]"}`} data-testid="zapi-sendtest-result">
                {zapiTestResult.ok
                  ? <>HTTP {zapiTestResult.http_status} · messageId: {zapiTestResult.message_id || "-"}</>
                  : <>HTTP {zapiTestResult.http_status ?? "-"} · {zapiTestResult.detail} {zapiTestResult.body ? <span className="block break-all">body: {zapiTestResult.body}</span> : null}</>}
              </div>
            )}
          </div>
        </div>

        {zapiQr && (
          <div className="grid place-items-center my-4 py-4 border border-dashed border-[#EBE5DF] bg-[#FAF7F3]" data-testid="zapi-qr">
            <img src={zapiQr} alt="QR Code WhatsApp oficial" className="w-56 h-56 border border-[#EBE5DF] bg-white" />
            <div className="text-xs text-[#5C5A57] mt-2 text-center max-w-sm">No celular do numero oficial: WhatsApp &gt; Aparelhos conectados &gt; Conectar aparelho e escaneie. O QR expira a cada ~20s.</div>
            <button onClick={showZapiQr} disabled={busy === "zapi-qr"} className="text-[10px] tracking-widest uppercase font-medium hover:underline inline-flex items-center gap-1 mt-2" data-testid="zapi-qr-refresh-btn"><ArrowsClockwise size={13} /> Atualizar QR Code</button>
          </div>
        )}

        {zapiStatusInfo && !zapiStatusInfo.connected && !zapiQr && (
          <div className="text-[11px] text-[#5C5A57] mt-3" data-testid="zapi-status-info">
            Status atual: {zapiStatusInfo.session_status || (zapiStatusInfo.needs_qr ? "aguardando conexao - gere o QR Code" : "indisponivel")}
          </div>
        )}
      </section>

      {/* Modo de operacao do PIX */}
      <section className="border border-[#EBE5DF] bg-[#F5F0EB] p-6" data-testid="pix-mode-section">
        <h2 className="section-title mb-1">Modo de operacao do PIX</h2>
        <div className="text-xs text-[#5C5A57] mb-4">Define qual token do Mercado Pago e usado ao gerar cada cobranca.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button onClick={() => saveMode("platform")} disabled={busy === "mode"} data-testid="pix-mode-platform"
            className={`text-left border p-4 transition-colors ${mode === "platform" ? "border-[#0A0A0A] bg-white" : "border-[#EBE5DF] bg-white/40"}`}>
            <div className="font-display text-lg font-bold">Plataforma unica</div>
            <div className="text-xs text-[#5C5A57] mt-1">Todas as cobrancas usam o token da plataforma. Operacao centralizada.</div>
          </button>
          <button onClick={() => saveMode("reseller")} disabled={busy === "mode"} data-testid="pix-mode-reseller"
            className={`text-left border p-4 transition-colors ${mode === "reseller" ? "border-[#0A0A0A] bg-white" : "border-[#EBE5DF] bg-white/40"}`}>
            <div className="font-display text-lg font-bold">Por revendedora</div>
            <div className="text-xs text-[#5C5A57] mt-1">Cada revendedora usa o proprio token. Operacao descentralizada.</div>
          </button>
        </div>
      </section>

      {/* Seguranca / Credenciais Administrativas */}
      <section className="border border-[#EBE5DF] bg-white p-6 mt-6" data-testid="security-section">
        <div className="flex items-center gap-3 mb-1">
          <ShieldCheck size={26} weight="duotone" />
          <h2 className="section-title">Seguranca / Credenciais Administrativas</h2>
        </div>
        <div className="text-xs text-[#5C5A57] mb-5">
          Altere seu e-mail e senha de acesso. Por seguranca, suas sessoes ativas serao encerradas e sera necessario fazer login novamente.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Trocar e-mail */}
          <div data-testid="change-email-form">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-3">Alterar e-mail de login</div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Senha atual</label>
            <input type="password" autoComplete="off" className="input-underline mb-4" value={curPwE} onChange={(e) => setCurPwE(e.target.value)} data-testid="email-current-password" />
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Novo e-mail</label>
            <input type="email" autoComplete="off" className="input-underline" placeholder="novo@crislingerie.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
            <button onClick={changeEmail} disabled={busy === "email"} className="brand mt-5" data-testid="change-email-btn">{busy === "email" ? "Salvando..." : "Alterar e-mail"}</button>
          </div>

          {/* Trocar senha */}
          <div data-testid="change-password-form">
            <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-3">Alterar senha</div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Senha atual</label>
            <input type="password" autoComplete="off" className="input-underline mb-4" value={curPwP} onChange={(e) => setCurPwP(e.target.value)} data-testid="pw-current-password" />
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nova senha (min. {minPw})</label>
            <input type="password" autoComplete="new-password" className="input-underline mb-4" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Confirmar nova senha</label>
            <input type="password" autoComplete="new-password" className="input-underline" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
            <button onClick={changePassword} disabled={busy === "pw"} className="brand mt-5" data-testid="change-password-btn">{busy === "pw" ? "Salvando..." : "Alterar senha"}</button>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-[#EBE5DF]">
          <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Tamanho minimo de senha</label>
          <div className="flex flex-wrap items-center gap-3">
            <input type="number" min="4" max="64" className="input-underline" style={{ maxWidth: 120 }} value={minPw} onChange={(e) => setMinPw(e.target.value)} data-testid="min-pw-input" />
            <button onClick={saveMinPw} disabled={busy === "minpw"} className="brand" style={{ background: "#5C5A57" }} data-testid="min-pw-save-btn">{busy === "minpw" ? "..." : "Salvar"}</button>
          </div>
        </div>
      </section>
    </div>
  );
}
